import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Droplets, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/lib/auth-context';
import { formatarDataHora } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

interface Lavagem {
  seq_lavagem: number;
  cod_status: string;
  cod_modalidade: string;
  num_placa_lida: string | null;
  qtd_segundos_duracao: number | null;
  dta_inicio: string;
  dta_fim: string | null;
}

/**
 * Histórico de lavagens — cards arejados com pill de status, hover lift e
 * tap feedback. Realtime atualiza status em tempo real.
 */
export function LavagensPage() {
  const { user } = useAuth();

  const lavagens = useQuery({
    queryKey: ['cliente-lavagens', user?.id],
    queryFn: async () => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente')
        .eq('cod_auth_user', user!.id)
        .single();
      if (!cli) return [];
      const { data, error } = await supabase
        .from('tab_lavagens')
        .select(
          'seq_lavagem, cod_status, cod_modalidade, num_placa_lida, qtd_segundos_duracao, dta_inicio, dta_fim',
        )
        .eq('seq_cliente', cli.seq_cliente)
        .order('dta_inicio', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as Lavagem[];
    },
    enabled: !!user,
  });

  // Realtime: status muda (INICIADA -> EM_ANDAMENTO -> CONCLUIDA) sem refresh
  useRealtimeInvalidate('tab_lavagens', ['cliente-lavagens', user?.id]);

  const total = lavagens.data?.length ?? 0;
  const emAndamento =
    lavagens.data?.filter((l) => l.cod_status === 'EM_ANDAMENTO' || l.cod_status === 'INICIADA')
      .length ?? 0;

  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Header de página */}
      <div className="octa-page-header octa-fade-in-up">
        <p className="text-overline text-muted-foreground uppercase">Histórico</p>
        <h1>Minhas lavagens</h1>
        {total > 0 && (
          <p>
            {total} lavagem{total > 1 ? 'ns' : ''} registrada{total > 1 ? 's' : ''}
            {emAndamento > 0 && (
              <>
                {' · '}
                <span className="text-primary font-semibold">{emAndamento} em andamento</span>
              </>
            )}
          </p>
        )}
      </div>

      {lavagens.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : lavagens.data?.length === 0 ? (
        <EmptyState
          variant="hero"
          icon={Droplets}
          title="Sem lavagens ainda"
          description="Vá a uma unidade e use seu código OU LPR automático e o histórico aparece aqui."
        />
      ) : (
        <div className="octa-stagger space-y-3">
          {lavagens.data?.map((l) => {
            const status = l.cod_status;
            const isRunning = status === 'EM_ANDAMENTO' || status === 'INICIADA';
            const isDone = status === 'CONCLUIDA';
            const variant: 'success' | 'info' | 'danger' | 'neutral' = isDone
              ? 'success'
              : isRunning
                ? 'info'
                : status === 'CANCELADA' || status === 'FALHA'
                  ? 'danger'
                  : 'neutral';

            return (
              <Link
                key={l.seq_lavagem}
                to={`/lavagem/${l.seq_lavagem}`}
                className="octa-card-tap block"
                aria-label={`Detalhes da lavagem de ${l.num_placa_lida ?? 'sem placa'}`}
              >
                <Card variant="elevated" className="overflow-hidden">
                  <CardContent className="flex items-center gap-3 p-4">
                    {/* Ícone destacado com gradient */}
                    <div
                      className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                        isDone
                          ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
                          : isRunning
                            ? 'bg-octa-gradient text-white'
                            : 'bg-muted text-muted-foreground'
                      } shadow-octa-sm`}
                    >
                      {isDone ? (
                        <Sparkles className="h-5 w-5" />
                      ) : (
                        <Droplets className={`h-5 w-5 ${isRunning ? 'animate-pulse' : ''}`} />
                      )}
                      {isRunning && (
                        <span className="ring-card absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2">
                          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/60" />
                        </span>
                      )}
                    </div>

                    {/* Conteúdo principal */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-base font-extrabold tracking-wider">
                          {l.num_placa_lida ?? '—'}
                        </p>
                        <StatusBadge variant={variant} dot pulse={isRunning}>
                          {status}
                        </StatusBadge>
                      </div>
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        {formatarDataHora(l.dta_inicio)}
                      </p>
                      <p className="text-muted-foreground text-[11px]">
                        {l.cod_modalidade}
                        {l.qtd_segundos_duracao &&
                          ` · ${Math.round(l.qtd_segundos_duracao / 60)}min`}
                      </p>
                    </div>

                    <ChevronRight className="text-muted-foreground/60 h-4 w-4 shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
